import {
    FC,
    useEffect,
    useState,
    useCallback,
    useContext,
    useRef,
    MutableRefObject,
    MouseEventHandler,
} from 'react';
import {
    ConnectionConfig,
    createPaginationContainer,
    graphql,
    RelayPaginationProp,
} from 'react-relay';
import classnames from 'classnames';

// components
import { Button } from 'dibs-elements/exports/Button';
import { Link } from 'dibs-elements/exports/Link';
import { SeoLink } from 'dibs-seo/exports/SeoLink';
import { Spinner } from 'dibs-elements/exports/Spinner';
import { breakpoints } from 'dibs-sassy/exports/breakpoints';
import { Visibility } from '../../components/global/Visibility';
import { ServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext';
import { PdpRespSimilarItemsGrid } from '../PdpRespSimilarItems/PdpRespSimilarItemsGrid';

// helpers
import { filterNulls } from 'dibs-ts-utils/exports/filterNulls';
import { Maybe } from 'dibs-ts-utils/exports/Maybe';
import { flattenConnectionNodes, Connection } from '../../utils/relayUtils';
import { locations } from '../../utils/tracking/shared/favoritesTracking';
import {
    SIMILAR_ITEMS_LIST,
    VISUALLY_SIMILAR_ITEMS_LIST,
    COLLECTION_MODULE_ITEMS_LIST,
    PERSONALIZATION_MODULE_ITEMS_LIST,
    trackBottomYmalModuleReached,
    trackCollectionItemsVisible,
    trackCollectionItemsRendered,
    trackViewAllCollectionItemsClick,
    trackViewMoreVisuallySimilarClick,
    trackViewMoreRelatedItemsClick,
    trackViewLessRelatedItemsClick,
} from '../tracking/similarItemsTracking';

// messages
import {
    peopleAlsoBrowsed,
    visuallySimilar,
    viewAllVisuallySimilar,
    viewAllInCollection,
    FeaturedInCollection,
} from '../pdpMessages';
import { youMayAlsoLike, viewMore, viewLess } from '../../shared/sharedMessages';

import { PdpRespSimilarItemsFooter_viewer$data } from './__generated__/PdpRespSimilarItemsFooter_viewer.graphql';
import { PdpRespSimilarItemsFooter_user$data } from './__generated__/PdpRespSimilarItemsFooter_user.graphql';
import { PdpRespSimilarItemsFooter_item$data } from './__generated__/PdpRespSimilarItemsFooter_item.graphql';

import styles from './PdpRespSimilarItemsFooter.scss';

const MAX_VISUALLY_SIMILAR_GRID_ITEMS = 5;
const MAX_VISUALLY_SIMILAR_TABLET_GRID_ITEMS = 4;

function removeDupeItems<T extends { serviceId: string | null }>(
    ids: Maybe<string>[],
    connection: Connection<T | null>,
    isTabletResolution: Maybe<boolean>
): T[] {
    return flattenConnectionNodes(connection)
        .filter(item => !ids.includes(item.serviceId))
        .splice(
            0,
            isTabletResolution
                ? MAX_VISUALLY_SIMILAR_TABLET_GRID_ITEMS
                : MAX_VISUALLY_SIMILAR_GRID_ITEMS
        );
}

const PAGE_SIZE = 10;
const ITEMS_PER_ROW = 5;

const handleGridScroll = (tileRef: MutableRefObject<HTMLDivElement | undefined>): void => {
    if (tileRef?.current) {
        window.scrollTo(
            window.scrollX,
            window.scrollY - (PAGE_SIZE / ITEMS_PER_ROW) * tileRef.current.clientHeight
        );
    }
};

type RelayVariables = {
    userId?: string;
    itemId: string;
    isClient: boolean;
    isTrade: boolean;
    userCountryCode: string;
};

type Props = {
    currency?: string;
    viewer: PdpRespSimilarItemsFooter_viewer$data;
    user: PdpRespSimilarItemsFooter_user$data | null;
    item: PdpRespSimilarItemsFooter_item$data;
    relay: RelayPaginationProp;
    scrollToRef: MutableRefObject<HTMLDivElement | undefined>;
    relayVariables: RelayVariables;
};

export const PdpRespSimilarItemsFooterComponent: FC<Props> = ({
    currency,
    viewer,
    user,
    item,
    relayVariables,
    scrollToRef,
    relay,
}) => {
    const { isTablet } = useContext(ServerVarsContext);
    const [isExpanded, setIsExpanded] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [hasFetched, setHasFetched] = useState(false);
    const [isTabletResolution, setIsTabletResolution] = useState(isTablet);
    const gridItemWrapperRef = useRef<HTMLDivElement>();

    const { serviceId: userId } = user || {};
    const { visuallySimilarGridItems } = viewer || {};
    const {
        serviceId: itemId,
        isArt,
        isAuctionEnabled,
        isSold,
        isUnpurchasable,
        collectionModule,
    } = item;
    const { isClient } = relayVariables;

    const similarItems = flattenConnectionNodes(viewer?.similarItems);
    const similarItemsIds = similarItems.map(node => node.serviceId);

    const associatedItems = viewer.personalizedItems?.associatedItems || [];
    const personalizedItems = associatedItems
        .map(association => association?.item || null)
        .filter(filterNulls);

    const collectionItems = flattenConnectionNodes(collectionModule?.items);

    const isUnavailable = isSold || isUnpurchasable;

    useEffect(() => {
        // TODO: when we will update relay worth revisiting if client check is still needed
        // At the moment If refetch happens and in the meantime relay vars changes, data gets disposed
        // more info: https://github.com/facebook/relay/blob/1.4.1-manual-patch/packages/react-relay/modern/ReactRelayRefetchContainer.js#L114-L118
        if (isClient && isArt && !isUnavailable && !visuallySimilarGridItems) {
            relay.refetchConnection(PAGE_SIZE, null, {
                fetchVisuallySimilar: true,
                count: PAGE_SIZE,
            });
        }
    }, [isArt, isUnavailable, relay, isClient, visuallySimilarGridItems]);

    useEffect(() => {
        if (userId) {
            // reset pagination container when user logs in to prevent broken pagination state
            setIsExpanded(false);
            setHasFetched(false);
        }
    }, [userId]);

    useEffect(() => {
        setIsTabletResolution(
            tabletResolution => tabletResolution || window.innerWidth < breakpoints.desktop.min
        );
    }, []);

    const sharedItemsGridProps = {
        relayVariables: {
            userId: relayVariables.userId,
            isClient: relayVariables.isClient,
        },
        item,
        viewer,
        currency,
        user,
        favoritesLocation: locations.SIMILAR_ITEM_GRID,
        gridItemWrapperClass: styles.similarItems,
        hideBar: true,
        showNoReserveBadge: false,
    };

    const getButtonState = useCallback(
        function () {
            if (isLoading) {
                return <Spinner size="tiny" containerClass={styles.spinner} />;
            } else if (isExpanded) {
                return viewLess;
            } else {
                return viewMore;
            }
        },
        [isLoading, isExpanded]
    );
    const onViewMoreClick = useCallback<MouseEventHandler>(
        function (event) {
            if (isExpanded) {
                trackViewLessRelatedItemsClick(event);
            } else {
                trackViewMoreRelatedItemsClick(event);
            }
            if (relay.hasMore() && !hasFetched) {
                setIsLoading(true);
                relay.loadMore(PAGE_SIZE, () => {
                    setIsLoading(false);
                    setIsExpanded(!isExpanded);
                    setHasFetched(true);
                });
            } else if (hasFetched) {
                setIsExpanded(!isExpanded);
                if (isExpanded) {
                    handleGridScroll(gridItemWrapperRef);
                }
            }
        },
        [isExpanded, relay, hasFetched]
    );

    const similarGridItems = (
        <>
            <PdpRespSimilarItemsGrid
                {...sharedItemsGridProps}
                header={youMayAlsoLike}
                scrollToRef={scrollToRef}
                gridLocation={isUnavailable ? 'sold pdp you may also like' : SIMILAR_ITEMS_LIST}
                similarItems={
                    isExpanded
                        ? similarItems
                        : similarItems.slice(0, isTabletResolution ? PAGE_SIZE - 2 : PAGE_SIZE)
                }
                gridItemWrapperRef={gridItemWrapperRef}
                isDark={!isUnavailable}
                gridHeaderClass={isUnavailable ? styles.gridHeaderSoldRedesign : undefined}
            />
            <div
                className={classnames(styles.wrapper, {
                    'primary-viewport-width': !isUnavailable,
                    [styles.isUnavailablePdp]: isUnavailable,
                })}
            >
                <Button
                    className={styles.button}
                    onClick={onViewMoreClick}
                    size="small"
                    type="transparent"
                    dataTn="pdp-similar-items-view-more-button"
                >
                    {getButtonState()}
                </Button>
            </div>
        </>
    );

    const showPersonalizationModule = personalizedItems.length > 0 && !isAuctionEnabled;
    const personalizationModuleItems = showPersonalizationModule && (
        <PdpRespSimilarItemsGrid
            {...sharedItemsGridProps}
            header={peopleAlsoBrowsed}
            gridLocation={PERSONALIZATION_MODULE_ITEMS_LIST}
            similarItems={personalizedItems}
            gridHeaderClass={isUnavailable ? styles.gridHeaderSoldRedesign : undefined}
            isDark={!isUnavailable}
            processGridItems={items =>
                items.map(({ ...processedItem }, index) => {
                    const variant = associatedItems[index]?.associationType;
                    if (variant) {
                        processedItem.ecommerceTrackingParams = {
                            ...(processedItem.ecommerceTrackingParams || {}),
                            variant,
                        };
                    }
                    return processedItem;
                })
            }
        />
    );

    const collectionModuleItems = collectionItems.length > 0 &&
        collectionModule?.name &&
        collectionModule?.linkData && (
            <PdpRespSimilarItemsGrid
                {...sharedItemsGridProps}
                gridHeaderClass={styles.visuallySimilarHeader}
                header={
                    <FeaturedInCollection
                        collectionName={collectionModule.name}
                        collectionNameClass={styles.gridHeaderItalics}
                    />
                }
                subHeader={
                    <div className={`primary-viewport-width ${styles.wrapper}`}>
                        <SeoLink
                            onClick={trackViewAllCollectionItemsClick}
                            linkData={collectionModule.linkData}
                            className={styles.viewMoreLink}
                        >
                            {viewAllInCollection}
                        </SeoLink>
                    </div>
                }
                gridLocation={COLLECTION_MODULE_ITEMS_LIST}
                similarItems={
                    isTabletResolution
                        ? collectionItems.splice(0, MAX_VISUALLY_SIMILAR_TABLET_GRID_ITEMS)
                        : collectionItems
                }
                onComponentDidMount={trackCollectionItemsRendered}
                onContentVisible={trackCollectionItemsVisible}
            />
        );

    const visuallySimilarItems = !collectionModuleItems &&
        !!visuallySimilarGridItems?.edges?.length && (
            <PdpRespSimilarItemsGrid
                {...sharedItemsGridProps}
                gridHeaderClass={styles.visuallySimilarHeader}
                header={visuallySimilar}
                subHeader={
                    <div className={`primary-viewport-width ${styles.wrapper}`}>
                        <Link
                            onClick={event => trackViewMoreVisuallySimilarClick(event)}
                            href={`/similar/?item=${itemId}`}
                            className={styles.viewMoreLink}
                        >
                            {viewAllVisuallySimilar}
                        </Link>
                    </div>
                }
                gridLocation={VISUALLY_SIMILAR_ITEMS_LIST}
                similarItems={removeDupeItems(
                    similarItemsIds,
                    visuallySimilarGridItems,
                    isTabletResolution
                )}
            />
        );

    return (
        <div className={styles.container}>
            {similarGridItems}
            {personalizationModuleItems}
            {collectionModuleItems}
            {visuallySimilarItems}
            <Visibility onContentVisible={trackBottomYmalModuleReached} />
        </div>
    );
};

const connectionConfig: ConnectionConfig<Omit<Props, 'relay'>> = {
    direction: 'forward',
    getConnectionFromProps(props) {
        return props.viewer && props.viewer.similarItems;
    },
    getFragmentVariables(prevVars, totalCount) {
        return {
            ...prevVars,
            count: totalCount,
        };
    },
    getVariables({ relayVariables }, { count, cursor }, fragmentVariables) {
        return {
            count,
            cursor,
            fetchVisuallySimilar: fragmentVariables.fetchVisuallySimilar,
            itemId: fragmentVariables.itemId,
            isTrade: relayVariables.isTrade,
            userCountryCode: relayVariables.userCountryCode,
        };
    },
    query: graphql`
        query PdpRespSimilarItemsFooterQuery(
            $fetchVisuallySimilar: Boolean!
            $itemId: String!
            $count: Int
            $cursor: String
            $isTrade: Boolean!
            $userCountryCode: String
            # TOF-419: Add personalization module
            $personalizationModuleEnabled: Boolean = false
        ) {
            viewer {
                ...PdpRespSimilarItemsFooter_viewer
                    @arguments(
                        fetchVisuallySimilar: $fetchVisuallySimilar
                        count: $count
                        cursor: $cursor
                    )
            }
        }
    `,
};

export const PdpRespSimilarItemsFooter = createPaginationContainer(
    PdpRespSimilarItemsFooterComponent,
    {
        viewer: graphql`
            fragment PdpRespSimilarItemsFooter_viewer on Viewer
            @argumentDefinitions(
                fetchVisuallySimilar: { type: "Boolean", defaultValue: false }
                count: { type: "Int", defaultValue: 10 }
                cursor: { type: "String" }
            ) {
                visuallySimilarGridItems: visualSearch(itemPk: $itemId, first: 6)
                    @include(if: $fetchVisuallySimilar) {
                    edges {
                        node {
                            serviceId
                            ...PdpRespSimilarItemsGrid_similarItems
                        }
                    }
                }
                similarItems: moreLikeThisItem(
                    first: $count
                    after: $cursor
                    seller: EXCLUDE
                    itemId: $itemId
                    excludeAuctionItems: true
                ) @connection(key: "PdpRespSimilarItemsFooter_similarItems") {
                    edges {
                        node {
                            serviceId
                            ...PdpRespSimilarItemsGrid_similarItems
                        }
                    }
                    pageInfo {
                        hasNextPage
                        startCursor
                        endCursor
                    }
                    totalResults
                }
                personalizedItems(itemId: $itemId)
                    # TOF-419: Remove directive at cleanup and replace with @include(if: $fetchPdp)
                    @include(if: $personalizationModuleEnabled) {
                    associatedItems {
                        associationType
                        item {
                            ...PdpRespSimilarItemsGrid_similarItems
                        }
                    }
                }
                ...PdpRespSimilarItemsGrid_viewer
            }
        `,
        user: graphql`
            fragment PdpRespSimilarItemsFooter_user on User {
                serviceId
                ...PdpRespSimilarItemsGrid_user
            }
        `,
        item: graphql`
            fragment PdpRespSimilarItemsFooter_item on Item {
                isArt
                isSold
                isUnpurchasable
                serviceId
                isAuctionEnabled
                ...PdpRespSimilarItemsGrid_item
                collectionModule {
                    name
                    items(first: 5) {
                        totalResults
                        edges {
                            node {
                                ...PdpRespSimilarItemsGrid_similarItems
                            }
                        }
                    }
                    linkData {
                        ...SeoLink_linkData
                    }
                }
            }
        `,
    },
    connectionConfig
);
